import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';

import TextInput from '../../../../components/formFields/TextInput';
import { SaveButton, CancelButton } from '../../../../components/buttons';
import { resetPasswordRequest } from '../../services/LoginActions';
import * as types from '../../services/LoginActionType';
import SweetAlert from '../../../../components/sweetalert/SweetAlert';
import Loader from '../../../../components/loader/view/Loader';

const passwordSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .required('Please enter your current password.'),
  newPassword: yup
    .string()
    .required('Please enter a new password.')
    .min(8, 'Password must be at least 8 characters.')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Include A–Z, a–z, 0–9 & a special symbol in your password.')
      .notOneOf([yup.ref('currentPassword')], 'New password cannot be the same as the current password.'),
  confirmPassword: yup
    .string()
    .required('Please confirm your new password.')
    .oneOf([yup.ref('newPassword')], 'Confirmed password must be the same as the new password.')
});



const ResetPassword = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const userData = useSelector(state => state.auth.userData);
  const { resetPasswordSuccess, resetPasswordError, isResetPasswordLoading } = useSelector(state => state.auth);
  
  // Only show reset password for accounts that actually have a password set
  const hasPassword = userData?.hasPassword || false;
  const shouldShowResetPassword = hasPassword && show;
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(passwordSchema)
  });

  useEffect(() => {
  if (resetPasswordSuccess) {
    SweetAlert.success({
      title: "Password Reset",
      text: "Password reset successful.",
    });
    handleClose();
  } else if (resetPasswordError) {
    const msg =
      typeof resetPasswordError === "string"
        ? resetPasswordError
        : resetPasswordError?.message || "Password reset failed.";

    SweetAlert.error({
      title: "Password Reset Failed",
      text: msg,
    });
  }
}, [resetPasswordSuccess, resetPasswordError]);

  const handleClose = () => {
    reset();
    // Clear all auth alert states to prevent stale messages
    dispatch({ type: types.CLEAR_AUTH_ALERTS });
    onClose && onClose();
  };

  const handleFormSubmit = (data) => {
    if (!userData?.userId) return;
    const payload = {
      UserId: userData.userId,
      CurrentPassword: data.currentPassword,
      NewPassword: data.newPassword
    };
    dispatch(resetPasswordRequest(payload));
  };

  return (
    <>
      {shouldShowResetPassword && (
        <Loader show={isResetPasswordLoading}>
          <div className={`modal fade${shouldShowResetPassword ? ' show' : ''}`} style={{ display: shouldShowResetPassword ? 'block' : 'none' }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reset Password</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleClose}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                  <div className="modal-body">
                    <TextInput
                      label="Current Password"
                      name="currentPassword"
                      type="password"
                      placeholder="Enter Current Password"
                      register={register}
                      errors={errors}
                      required={true}
                      stackLayout={true}
                    />
                    <TextInput
                      label="New Password"
                      name="newPassword"
                      type="password"
                      placeholder="Enter New Password"
                      register={register}
                      errors={errors}
                      required={true}
                      stackLayout={true}
                    />
                    <TextInput
                      label="Confirm New Password"
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm New Password"
                      register={register}
                      errors={errors}
                      required={true}
                      stackLayout={true}
                    />
                  </div>
                  <div className="modal-footer">
                    <CancelButton onClick={handleClose} />
                    <SaveButton type="submit" title="Reset Password" />
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </Loader>
      )}
    </>
  );
};

export default ResetPassword;
